import { FilterDefaultValue } from '../AIContent/_type';
import { isVersion2 } from '@/pages/aigc/utils/utils';
import md5 from 'crypto-js/md5';

/**
 * Markdown 中解析出的 BI 数据配置信息
 */
interface IDataBrainBiDataInfo {
  chat_type?: string;
  chart_type?: string;
  filter_info?: any[];
  filter_default_value?: FilterDefaultValue;
  title?: string;
  format?: string;
}

/**
 * 额外的数据源配置信息
 */
interface IDataSourceExtraInfo {
  from?: string;
  chartHeight?: number | string;
}

/**
 * 数据源对象结构
 */
interface IDataSource {
  data_id: string;
  data: {
    version?: string | number;
    chartType?: string;
    filter_info?: any[];
    filter_default_value?: FilterDefaultValue;
    title?: string;
    format?: string;
    [key: string]: any;
  };
  title?: string;
  from?: string;
  chartHeight?: number | string;
  [key: string]: any;
}

/**
 * 格式化 dataSource，优先使用 dataBrainBiDataInfo 中的配置字段（仅处理 v2 格式）
 * @param dataSource - 原始数据源对象
 * @param dataBrainBiDataInfo - Markdown 中解析出的配置信息
 * @param extraInfo - 额外的数据源配置信息（from、chartHeight 等）
 * @returns 格式化后的 dataSource
 */
export const formatDataSourceWithBiDataInfo = (
  dataSource: IDataSource,
  dataBrainBiDataInfo: IDataBrainBiDataInfo,
  extraInfo?: IDataSourceExtraInfo,
): IDataSource => {

  // 参数校验
  if (!dataSource?.data || !dataBrainBiDataInfo) {
    return dataSource;
  }
  const data = Array.isArray(dataSource?.data) ? dataSource?.data?.[0] : dataSource?.data;

  // 仅处理 v2 格式数据
  if (!isVersion2(data)) {
    // 即使不是 v2 格式，也设置 title、from、chartHeight 等字段
    // v2 格式：合并配置，优先使用 dataBrainBiDataInfo 中的字段
    const formatValue = dataBrainBiDataInfo?.format ?? dataSource?.data?.format;
    return {
      ...dataSource,
      title: dataBrainBiDataInfo?.title ?? dataSource?.title,
      from: extraInfo?.from ?? dataSource?.from,
      chartHeight: extraInfo?.chartHeight ?? dataSource?.chartHeight,
      data: {
        ...dataSource?.data,
        title: dataBrainBiDataInfo?.title ?? dataSource?.data?.title,
        chat_type: dataBrainBiDataInfo?.chart_type ?? dataBrainBiDataInfo?.chat_type ?? dataSource?.data?.chat_type,
      },
      format: formatValue ? formatValue?.toLowerCase() : formatValue,
    };
  }

  // v2 格式：合并配置，优先使用 dataBrainBiDataInfo 中的字段
  const formatValue = dataBrainBiDataInfo?.format ?? dataSource?.data?.format;
  return {
    ...dataSource,
    title: dataBrainBiDataInfo?.title ?? dataSource?.title,
    from: extraInfo?.from ?? dataSource?.from,
    chartHeight: extraInfo?.chartHeight ?? dataSource?.chartHeight,
    data: {
      ...dataSource?.data,
      title: dataBrainBiDataInfo?.title ?? dataSource?.data?.title,
      chat_type: dataBrainBiDataInfo?.chart_type ?? dataBrainBiDataInfo?.chat_type ?? dataSource?.data?.chat_type,
      filter_info: dataBrainBiDataInfo?.filter_info ?? dataSource?.data?.filter_info,
      filter_default_value:
        dataBrainBiDataInfo?.filter_default_value ??
        dataSource?.data?.filter_default_value,
    },
    format: formatValue ? formatValue?.toLowerCase() : formatValue,
  };
};

/**
 * 获取 data_id 和 filter_info 的唯一键
 * 使用 MD5 哈希生成固定长度的键，避免 filter_info 很大时键过长的问题
 * @param data - 包含 data_id 和 filter_info 的数据对象
 * @returns 固定长度的唯一键（data_id + MD5哈希）
 */
export const getDataFilterKey = (data: any): string => {
  const dataId = data?.data_id || '';
  const filterInfo = data?.filter_info || {};
  
  // 如果 filter_info 为空，直接返回 data_id
  if (!filterInfo || (Array.isArray(filterInfo) && filterInfo.length === 0) || 
      (typeof filterInfo === 'object' && Object.keys(filterInfo).length === 0)) {
    return dataId;
  }
  
  // 将 filter_info 序列化为字符串并生成 MD5 哈希
  const filterInfoStr = JSON.stringify(filterInfo);
  const hash = md5(filterInfoStr).toString();
  
  // 返回 data_id + 哈希值，固定长度
  return `${dataId}_${hash}`;
};

/**
 * 从 URL / 文件名中提取小写后缀（不含点号）。
 * 无后缀时返回空字符串。
 *
 * 示例：
 * - getFileExtension('xxx/a.HTML')       => 'html'
 * - getFileExtension('xxx/a.zip')        => 'zip'
 * - getFileExtension('xxx/preview')      => ''（通过回退判空）
 */
export const getFileExtension = (filename: string): string =>
  filename?.split('.').pop()?.toLowerCase() || '';

/** 解析 COS 下载卡片后缀：优先文件名，其次 URL 路径（忽略 query/hash） */
export const resolveCosFileExtension = (
  cosUrl: string,
  fileName?: string,
): string => {
  const nameExt = getFileExtension(fileName || '');
  if (nameExt) return nameExt;
  const urlPath = (cosUrl || '').split('?')[0].split('#')[0];
  return getFileExtension(urlPath);
};